import { FormikErrors, setIn, yupToFormErrors } from "formik";
import * as Yup from "yup";
import { ApplyType } from "../../redux/reducer/directJobs.types";
import { JobFormValues, parseAmount, toLocalDateString } from "./jobForm";

export type ServerFieldError = {
  loc: (string | number)[];
  msg: string;
  type?: string;
  ctx?: Record<string, unknown>;
};

const TITLE_MAX_LENGTH = 200;
const TEXT_MAX_LENGTH = 256;
const EMAIL_MAX_LENGTH = 254;
const URL_MAX_LENGTH = 1024;
const AMOUNT_MAX = 1_000_000_000;

const PLAIN_TEXT_MESSAGE = "Remove the < and > characters";
const EMAIL_MESSAGE = "Enter a valid email address";
const HTTPS_MESSAGE = "Enter a URL that starts with https://";
const DECIMALS_MESSAGE = "Enter a number with at most 2 decimals";
const NOT_NEGATIVE_MESSAGE = "Enter 0 or more";
const AMOUNT_MAX_MESSAGE = "Enter a smaller amount";
const SALARY_TO_MESSAGE = "Must not be lower than From";
const SHOW_UNTIL_MESSAGE = "Show until must not be before Show from";
const SHOW_UNTIL_PAST_MESSAGE = "Show until must be today or later";

const lengthMessage = (maxLength: number) =>
  `Use at most ${maxLength} characters`;

const plainText = (maxLength = TEXT_MAX_LENGTH) =>
  Yup.string()
    .matches(/^[^<>]*$/, { message: PLAIN_TEXT_MESSAGE })
    .max(maxLength, lengthMessage(maxLength));

const email = () =>
  Yup.string()
    .email(EMAIL_MESSAGE)
    .max(EMAIL_MAX_LENGTH, lengthMessage(EMAIL_MAX_LENGTH));

const hasText = (html: string | undefined) =>
  Boolean(
    html
      ?.replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim()
  );

const amountSchema = () =>
  Yup.string()
    .matches(/^-?\d*([.,]\d{0,2})?$/, {
      message: DECIMALS_MESSAGE,
      excludeEmptyString: true,
    })
    .test(
      "not-negative",
      NOT_NEGATIVE_MESSAGE,
      (value) => !value?.startsWith("-")
    )
    .test(
      "not-above-max",
      AMOUNT_MAX_MESSAGE,
      (value) => !value || parseAmount(value) <= AMOUNT_MAX
    );

const hasAmount = (salary: JobFormValues["salary"]) =>
  Boolean(salary.from || salary.to);

const jobFormSchema = (publish: boolean) =>
  Yup.object({
    title: plainText(TITLE_MAX_LENGTH).trim().required("Job title is required"),
    department: plainText(),
    category: plainText(),
    location: publish
      ? plainText().required("Location is required")
      : plainText(),
    ad_text: Yup.string().test(
      "has-text",
      "Ad text is required",
      (value) => !publish || hasText(value)
    ),
    salary: Yup.object({
      currency: Yup.string().test(
        "currency-for-amount",
        "Choose a currency",
        (value, context) => Boolean(value) || !hasAmount(context.parent)
      ),
      from: amountSchema(),
      to: amountSchema().test(
        "not-below-from",
        SALARY_TO_MESSAGE,
        (value, context) =>
          !value ||
          !context.parent.from ||
          parseAmount(value) >= parseAmount(context.parent.from)
      ),
      period: Yup.string().test(
        "period-for-amount",
        "Choose a period",
        (value, context) => Boolean(value) || !hasAmount(context.parent)
      ),
    }),
    contact_persons: Yup.array().of(
      Yup.object({
        name: plainText().required("Name is required"),
        title: plainText(),
        email: email(),
        phone: plainText(),
      })
    ),
    apply: Yup.object({
      url: Yup.string().when("type", {
        is: ApplyType.URL,
        then: (schema) => {
          const https = schema
            .matches(/^https:\/\/\S+$/i, {
              message: HTTPS_MESSAGE,
              excludeEmptyString: true,
            })
            .max(URL_MAX_LENGTH, lengthMessage(URL_MAX_LENGTH));
          return publish
            ? https.required("Application page URL is required")
            : https;
        },
      }),
      email: Yup.string().when("type", {
        is: ApplyType.EMAIL,
        then: () =>
          publish ? email().required("Email address is required") : email(),
      }),
    }),
    job_ad_template_id: publish
      ? Yup.string().required("Choose a job ad template")
      : Yup.string(),
    show_until: Yup.string()
      .test(
        "after-show-from",
        SHOW_UNTIL_MESSAGE,
        (value, context) =>
          !value ||
          !context.parent.show_from ||
          value >= context.parent.show_from
      )
      .test(
        "not-in-the-past",
        SHOW_UNTIL_PAST_MESSAGE,
        (value) => !publish || !value || value >= toLocalDateString(new Date())
      ),
  });

export const validateJobForm = async (
  values: JobFormValues,
  publish: boolean
): Promise<FormikErrors<JobFormValues>> => {
  try {
    await jobFormSchema(publish).validate(values, { abortEarly: false });
    return {};
  } catch (error) {
    return yupToFormErrors(error);
  }
};

const isServerFieldError = (value: unknown): value is ServerFieldError =>
  typeof value === "object" &&
  value !== null &&
  "loc" in value &&
  Array.isArray(value.loc) &&
  "msg" in value &&
  typeof value.msg === "string";

export const fieldErrorsOf = (error: unknown): ServerFieldError[] => {
  if (
    typeof error !== "object" ||
    error === null ||
    !("status" in error) ||
    error.status !== 422 ||
    !("data" in error) ||
    typeof error.data !== "object" ||
    error.data === null ||
    !("detail" in error.data) ||
    !Array.isArray(error.data.detail)
  ) {
    return [];
  }
  return error.data.detail.filter(isServerFieldError);
};

const VALUE_ERROR_PREFIX = "Value error, ";

const VALIDATOR_MESSAGES: Record<string, string> = {
  "must not contain < or >": PLAIN_TEXT_MESSAGE,
  "must be an email address": EMAIL_MESSAGE,
  "must be an https:// URL": HTTPS_MESSAGE,
  "must have at most 2 decimals": DECIMALS_MESSAGE,
  "must not be below from": SALARY_TO_MESSAGE,
  "an amount needs a currency and a period": "Choose a currency and a period",
  "must be after show_from": SHOW_UNTIL_MESSAGE,
};

const capitalize = (text: string) =>
  text.charAt(0).toUpperCase() + text.slice(1);

export const userMessage = ({ type, msg, ctx }: ServerFieldError): string => {
  switch (type) {
    case undefined:
    case "missing":
      return msg;
    case "value_error": {
      const text = msg.replace(VALUE_ERROR_PREFIX, "");
      return VALIDATOR_MESSAGES[text] ?? capitalize(text);
    }
    case "string_too_long":
      return lengthMessage(Number(ctx?.max_length));
    case "too_long":
      return `Add at most ${ctx?.max_length} contact persons`;
    case "less_than_equal":
      return AMOUNT_MAX_MESSAGE;
    case "greater_than_equal":
      return NOT_NEGATIVE_MESSAGE;
    default:
      return "Check this value";
  }
};

export const serverFieldErrors = (
  errors: ServerFieldError[]
): FormikErrors<JobFormValues> =>
  errors.reduce<FormikErrors<JobFormValues>>(
    (fieldErrors, error) =>
      setIn(fieldErrors, error.loc.slice(1).join("."), userMessage(error)),
    {}
  );
